import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { MATERIALS_TIG } from "@/i18n";
import { Zap, Wind, Wrench, Flame, Cpu, Loader2, AlertCircle, Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CalcActions from "@/components/CalcActions";
import CalcDisclaimer from "@/components/CalcDisclaimer";
import ProGate from "@/components/ProGate";
import GuestGate from "@/components/GuestGate";

const ELECTRODES = [1.0, 1.6, 2.4, 3.2];

export default function ManualTigCalc() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const isGuest = !user;
  const isFree = user && !user.is_subscribed && user.role !== "admin";
  const [params, setParams] = useSearchParams();
  const [material, setMaterial] = useState(params.get("material") || "stainless");
  const [thickness, setThickness] = useState(params.get("thickness") ? Number(params.get("thickness")) : 1.5);
  const [electrode, setElectrode] = useState(params.get("electrode_diameter") ? Number(params.get("electrode_diameter")) : 2.4);
  const [pipeDn, setPipeDn] = useState(params.get("pipe_dn") ? Number(params.get("pipe_dn")) : 25);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doCompute = async (vals) => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await api.post("/calc/manual-tig", vals);
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Compute failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get("material") || params.get("thickness") || params.get("electrode_diameter")) {
      doCompute({
        material: params.get("material") || material,
        thickness: parseFloat(params.get("thickness") || thickness),
        electrode_diameter: parseFloat(params.get("electrode_diameter") || electrode),
      });
    }
    // eslint-disable-next-line
  }, []);

  const compute = async (e) => {
    e?.preventDefault();
    const vals = { material, thickness: parseFloat(thickness), electrode_diameter: parseFloat(electrode), pipe_dn: parseFloat(pipeDn) || null };
    setParams({ material, thickness: String(thickness), electrode_diameter: String(electrode), pipe_dn: String(pipeDn) }, { replace: true });
    await doCompute(vals);
  };

  return (
    <div data-testid="tig-calc-page" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="mb-8">
        <span className="wb-tag !border-gold-600 !text-gold-500">MANUAL TIG</span>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2">{t.tig.title}</h1>
        <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider mt-2">{t.tig.sub}</p>
      </div>

      {isGuest && (
        <div className="mb-6 max-w-2xl">
          <GuestGate />
        </div>
      )}

      {isFree && (
        <div data-testid="tig-free-banner" className="mb-6 border-l-2 border-gold-600 bg-gold-500/5 p-4 flex items-start gap-3 text-sm">
          <span className="wb-tag !text-gold-400 !border-gold-600 mt-0.5">FREE</span>
          <div className="flex-1">
            <div className="font-display font-bold mb-1">Vyskúšaj zadarmo — limit do DN35</div>
            <p className="text-xs text-zinc-400">
              FREE režim: nehrdzavejúca / uhlíková oceľ, priemer rúry <strong className="text-white">DN ≤ 35</strong>, hrúbka steny <strong className="text-white">≤ 1.5 mm</strong>, elektróda do <strong className="text-white">2.4 mm</strong>. Nad limitom → <a href="/pricing" className="text-gold-400 hover:underline">PRO €3.99/týž</a>.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        <form onSubmit={compute} className="lg:col-span-5 border border-zinc-800 bg-[var(--wb-surface)] p-6" data-testid="tig-form">
          <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-gold-500" /> Inputs
          </h2>

          <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.tig.material}</label>
          <div className="grid grid-cols-2 gap-px bg-zinc-800 mb-4" data-testid="tig-material-selector">
            {MATERIALS_TIG.map((m) => (
              <button
                key={m.id} type="button"
                data-testid={`tig-material-${m.id}`}
                onClick={() => setMaterial(m.id)}
                className={`py-2 px-3 text-sm font-display uppercase tracking-wider ${material === m.id ? "bg-gold-600 text-white" : "bg-[var(--wb-bg)] text-zinc-400 hover:text-white"}`}
              >{m[lang]}</button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">Priemer rúry (DN)</label>
              <input
                data-testid="tig-pipe-dn-input"
                type="number" step="1" min="0"
                value={pipeDn} onChange={(e) => setPipeDn(e.target.value)}
                placeholder="Napr. 25"
                className="wb-input w-full px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.tig.thickness}</label>
              <input
                data-testid="tig-thickness-input"
                type="number" step="0.1" min="0.1" required
                value={thickness} onChange={(e) => setThickness(e.target.value)}
                className="wb-input w-full px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.tig.electrode}</label>
              <select
                data-testid="tig-electrode-select"
                value={electrode} onChange={(e) => setElectrode(e.target.value)}
                className="wb-input w-full px-3 py-2 text-sm font-mono"
              >
                {ELECTRODES.map((e) => <option key={e} value={e}>{e.toFixed(1)}</option>)}
              </select>
            </div>
          </div>

          <button
            data-testid="tig-compute-btn"
            type="submit" disabled={loading}
            className="wb-btn-primary w-full py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.common.loading}</> : t.tig.compute}
          </button>
          {error && error.startsWith("PRO_REQUIRED:") ? (
            <div className="mt-3"><ProGate compact message={error.replace("PRO_REQUIRED:", "").trim()} /></div>
          ) : error ? (
            <div data-testid="tig-error" className="mt-3 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          ) : null}
        </form>

        <div className="lg:col-span-7" data-testid="tig-result-section">
          {!result ? (
            <div className="h-full border border-zinc-800 border-dashed flex flex-col items-center justify-center min-h-[400px] text-zinc-600">
              <Activity className="w-10 h-10 mb-3" strokeWidth={1.2} />
              <p className="font-mono text-xs uppercase tracking-wider">{t.orbital.waitingCompute}</p>
            </div>
          ) : (
            <div className="space-y-px bg-zinc-800" data-testid="tig-result-block">
              <div className="bg-[var(--wb-surface)] p-6">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{t.tig.currentRange}</div>
                <div className="flex items-end gap-3">
                  <div className="font-display font-black text-5xl md:text-6xl text-gold-500" data-testid="metric-current-range">
                    {result.current_min_a}–{result.current_max_a}
                  </div>
                  <div className="font-mono text-zinc-500 pb-2">A</div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-px bg-zinc-800">
                <InfoCell label={t.tig.polarity} value={result.polarity} testid="info-polarity" />
                <InfoCell label={t.tig.gas} value={result.gas_type} testid="info-gas" />
                <InfoCell label={t.tig.gasFlow} value={`${result.gas_flow_l_min} l/min`} testid="info-gasflow" />
              </div>
              <div className="grid md:grid-cols-3 gap-px bg-zinc-800">
                <InfoCell label={t.tig.filler} value={result.filler_wire} testid="info-filler" />
                <InfoCell label={t.tig.fillerDiam} value={`${result.filler_diameter_mm} mm`} testid="info-filler-diam" />
                <InfoCell label={t.tig.preheat} value={result.preheat_c ? `${result.preheat_c} °C` : "—"} testid="info-preheat" />
              </div>
              <div className="bg-[var(--wb-surface)] p-5">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{t.tig.electrodeType}</div>
                <div className="text-sm">{result.electrode_type}</div>
              </div>
              {result.warning && (
                <div className="bg-amber-950/40 border border-amber-700 p-4 text-sm text-amber-300" data-testid="tig-warning">
                  ⚠ {result.warning}
                </div>
              )}
              <div className="bg-[var(--wb-surface)] p-4 text-xs text-zinc-500 font-mono">{result.notes}</div>
              <CalcActions
                type="manual_tig"
                inputs={{ material, thickness, electrode_diameter: electrode }}
                outputs={result}
                shareParams={{ material, thickness, electrode_diameter: electrode }}
                title={`TIG — ${material} · ${thickness}mm · ⌀${electrode}mm`}
              />
            </div>
          )}
        </div>
      </div>

      {/* Persistent disclaimer — always visible */}
      <div className="mt-6">
        <CalcDisclaimer />
      </div>
    </div>
  );
}

function InfoCell({ label, value, testid }) {
  return (
    <div className="bg-[var(--wb-surface)] p-5" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
